import { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, Search } from "lucide-react";
import { AgentStep } from "../../types";
import { motion, AnimatePresence } from "framer-motion";

interface AgenticProcessViewProps {
  steps: AgentStep[];
  memorySteps: Array<{ type: 'memory_query' | 'memory_store'; status: 'in_progress' | 'completed' | 'no_results'; message: string }>;
  isComplete: boolean;
  onComplete?: () => void;
}

// Convert steps to simple, Grok-like display format
const formatStepMessage = (step: AgentStep): string => {
  const action = step.action.toLowerCase();
  
  // Extract key information from action_input
  const input = step.action_input;
  let target = '';
  
  if (input) {
    if (typeof input === 'string') {
      target = input;
    } else if (input.query) {
      target = input.query;
    } else if (input.search_query) {
      target = input.search_query;
    } else if (input.question) {
      target = input.question;
    } else if (input.content) {
      target = input.content;
    } else if (input.page_title) {
      target = input.page_title;
    } else {
      // Get first string value from input
      const firstValue = Object.values(input).find(v => typeof v === 'string');
      if (firstValue) target = String(firstValue);
    }
  }
  
  // Keep messages very short and action-focused
  if (action.includes('search')) {
    if (action.includes('notion')) return `Searching Notion${target ? `: "${target.slice(0, 30)}..."` : ''}`;
    if (action.includes('email') || action.includes('gmail')) return `Searching emails${target ? `: "${target.slice(0, 30)}..."` : ''}`;
    if (action.includes('web')) return `Searching web${target ? `: "${target.slice(0, 30)}..."` : ''}`;
    if (action.includes('drive')) return `Searching Google Drive${target ? `: "${target.slice(0, 30)}..."` : ''}`;
    if (action.includes('calendar')) return `Searching calendar${target ? `: "${target.slice(0, 30)}..."` : ''}`;
    if (action.includes('memory')) return `Searching memory${target ? `: "${target.slice(0, 30)}..."` : ''}`;
    return `Searching${target ? `: "${target.slice(0, 30)}..."` : ''}`;
  }
  
  if (action.includes('create')) {
    if (action.includes('notion')) return `Creating Notion page${target ? `: "${target.slice(0, 30)}..."` : ''}`;
    return `Creating${target ? `: "${target.slice(0, 30)}..."` : ''}`;
  }
  
  if (action.includes('send')) {
    if (action.includes('email')) return `Sending email${target ? ` to ${target.slice(0, 30)}...` : ''}`;
    return `Sending${target ? `: "${target.slice(0, 30)}..."` : ''}`;
  }
  
  if (action.includes('store') && action.includes('memory')) {
    return `Storing in memory${target ? `: "${target.slice(0, 30)}..."` : ''}`;
  }
  
  if (action === 'finish') {
    return 'Finalizing response';
  }
  
  // Fallback: clean up action name
  return action.replace(/_/g, ' ').charAt(0).toUpperCase() + action.slice(1).replace(/_/g, ' ');
}

export function AgenticProcessView({ steps, memorySteps, isComplete, onComplete }: AgenticProcessViewProps) {
  const [isOpen, setIsOpen] = useState(true);

  // Auto-collapse when complete
  useEffect(() => {
    if (isComplete && onComplete) {
      const timer = setTimeout(() => {
        setIsOpen(false);
        onComplete();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isComplete, onComplete]);

  if (steps.length === 0 && memorySteps.length === 0) return null;

  return (
    <div className="mb-3">
      <div className="bg-blue-600/10 backdrop-blur-sm border border-blue-500/20 rounded-xl shadow-lg shadow-blue-500/5">
        {/* Collapsible Header - Blue glass style */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-blue-500/15 transition-all duration-200 rounded-xl"
        >
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-blue-500/30 rounded-lg flex items-center justify-center backdrop-blur-sm">
              <Search className="h-3 w-3 text-blue-300" />
            </div>
            <span className="text-blue-100 text-sm font-medium">Processing request</span>
          </div>
          {isOpen ? 
            <ChevronDown className="h-4 w-4 text-blue-300 transition-transform duration-200" /> : 
            <ChevronRight className="h-4 w-4 text-blue-300 transition-transform duration-200" />
          }
        </button>
        
        {/* Collapsible Content - Simple list style like Grok */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-2 bg-blue-500/5 rounded-b-xl max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-blue-500/20 scrollbar-track-transparent">
                {/* Memory steps if any */}
                {memorySteps.map((step, index) => (
                  <motion.div
                    key={`memory-${index}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(index * 0.05, 0.3) }}
                    className="flex items-center gap-3 text-sm py-2 px-2 rounded-lg hover:bg-blue-500/10 transition-all duration-200"
                  >
                    <div className="w-5 h-5 bg-purple-400/30 rounded-lg flex items-center justify-center backdrop-blur-sm">
                      <div className="h-2 w-2 rounded-full bg-purple-300" />
                    </div>
                    <span className="text-blue-200/80">{step.message}</span>
                  </motion.div>
                ))}
                
                {/* Agent steps - Blue glass style */}
                {steps.map((step, index) => {
                  const message = formatStepMessage(step);
                  const isSearching = step.action.toLowerCase().includes('search');
                  const isCompleted = step.action === 'finish' || index < steps.length - 1;
                  
                  return (
                    <motion.div
                      key={`step-${index}`}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: Math.min((index + memorySteps.length) * 0.05, 0.5) }}
                      className="flex items-center gap-3 text-sm py-2 px-2 rounded-lg hover:bg-blue-500/10 transition-all duration-200"
                    >
                      <div className="flex-shrink-0">
                        {isSearching ? (
                          <div className="w-5 h-5 bg-blue-400/30 rounded-lg flex items-center justify-center backdrop-blur-sm">
                            <Search className="h-3 w-3 text-blue-200" />
                          </div>
                        ) : isCompleted ? (
                          <div className="w-5 h-5 bg-emerald-400/30 rounded-lg flex items-center justify-center backdrop-blur-sm">
                            <div className="h-2 w-2 rounded-full bg-emerald-300" />
                          </div>
                        ) : (
                          <div className="w-5 h-5 bg-blue-500/30 rounded-lg flex items-center justify-center backdrop-blur-sm">
                            <div className="h-2 w-2 rounded-full bg-blue-300 animate-pulse" />
                          </div>
                        )}
                      </div>
                      <span className={`${isCompleted ? 'text-blue-200/80' : 'text-blue-100 font-medium'}`}>
                        {message}
                      </span>
                      {/* Show completion check for finished steps */}
                      {isComplete && index === steps.length - 1 && (
                        <span className="text-emerald-400 text-xs ml-auto">✓</span>
                      )}
                      {/* Show spinner for current step */}
                      {!isCompleted && (
                        <div className="flex items-center gap-1 ml-auto">
                          <div className="w-2 h-2 bg-blue-300 rounded-full animate-pulse" />
                          <div className="w-2 h-2 bg-blue-300 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                          <div className="w-2 h-2 bg-blue-300 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
} 